import { z } from 'zod/mini';

/**
 * A server can be private: starting a game (solo or party) then needs its access code, while
 * joining a party with its code stays open to anyone invited.
 */
export const ACCESS_DENIED_MESSAGE = 'This server is private: enter the access code to start a game.';

/** Request header carrying the token a browser gets in exchange for the access code. */
export const ACCESS_TOKEN_HEADER = 'x-access-token';

export const accessCodeSchema = z.object({
  code: z.string().check(z.trim(), z.minLength(1, 'Enter the access code.'), z.maxLength(200)),
});

export const accessTokenSchema = z.string().check(z.maxLength(200));

/** Who a token belongs to: the owner (permanent code) or a guest (temporary code). */
export type AccessRole = 'owner' | 'guest';

/** The grant written in tokens unlocked with the permanent code; guest tokens carry their code's id. */
export const OWNER_GRANT = 'owner';

/** Tokens read `<expiresAt>.<grant>.<signature>`. Only the server can check the signature. */
export function parseAccessToken(token: string): { expiresAt: number; grant: string; signature: string } | null {
  const [expires, grant, signature, ...rest] = token.split('.');
  const expiresAt = Number(expires);
  if (rest.length > 0 || !grant || !signature || !Number.isSafeInteger(expiresAt)) return null;
  return { expiresAt, grant, signature };
}

/** A temporary code the owner hands out: it unlocks game creation until it expires. */
export interface GuestCode {
  id: string;
  code: string;
  label: string | null;
  createdAt: number;
  expiresAt: number;
}

/** How long a guest code can last, in days. */
export const GUEST_CODE_DAYS = [1, 2, 3] as const;

export const createGuestCodeSchema = z.object({
  label: z.optional(z.string().check(z.trim(), z.maxLength(40))),
  days: z.int().check(z.minimum(GUEST_CODE_DAYS[0]), z.maximum(GUEST_CODE_DAYS.at(-1)!)),
});
